import {
  Action,
  ActionReducer,
  ActionReducerMap,
  ActionReducerFactory,
  MetaReducer,
} from './models';

export function combineReducers<T, V extends Action = Action>(
  reducers: ActionReducerMap<T, V>,
  initialState?: Partial<T>
): ActionReducer<T, V>;
/**
 * @param {?} reducers
 * @param {?=} initialState
 * @return {?}
 */
export function combineReducers(
  reducers: any,
  initialState: any = {}
): ActionReducer<any, Action> {
  const /** @type {?} */ reducerKeys = Object.keys(reducers);
  const /** @type {?} */ finalReducers: any = {};

  for (let /** @type {?} */ i = 0; i < reducerKeys.length; i++) {
    const /** @type {?} */ key = reducerKeys[i];
    if (typeof reducers[key] === 'function') {
      finalReducers[key] = reducers[key];
    }
  }

  const /** @type {?} */ finalReducerKeys = Object.keys(finalReducers);

  return function combination(state = initialState, action) {
    let /** @type {?} */ hasChanged = false;
    const /** @type {?} */ nextState: any = {};
    for (let /** @type {?} */ i = 0; i < finalReducerKeys.length; i++) {
      const /** @type {?} */ key = finalReducerKeys[i];
      const /** @type {?} */ reducer: any = finalReducers[key];
      const /** @type {?} */ previousStateForKey = state[key];
      const /** @type {?} */ nextStateForKey = reducer(previousStateForKey, action);

      nextState[key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }
    return hasChanged ? nextState : state;
  };
}
/**
 * @template T
 * @param {?} object
 * @param {?} keyToRemove
 * @return {?}
 */
export function omit<T extends { [key: string]: any }>(
  object: T,
  keyToRemove: keyof T
): Partial<T> {
  return Object.keys(object)
    .filter(key => key !== keyToRemove)
    .reduce((result, key) => Object.assign(result, { [key]: object[key] }), {});
}

export function compose<A>(): (i: A) => A;
export function compose<A, B>(b: (i: A) => B): (i: A) => B;
export function compose<A, B, C>(c: (i: B) => C, b: (i: A) => B): (i: A) => C;
export function compose<A, B, C, D>(
  d: (i: C) => D,
  c: (i: B) => C,
  b: (i: A) => B
): (i: A) => D;
export function compose<A, B, C, D, E>(
  e: (i: D) => E,
  d: (i: C) => D,
  c: (i: B) => C,
  b: (i: A) => B
): (i: A) => E;
export function compose<A, B, C, D, E, F>(
  f: (i: E) => F,
  e: (i: D) => E,
  d: (i: C) => D,
  c: (i: B) => C,
  b: (i: A) => B
): (i: A) => F;
/**
 * @param {...?} functions
 * @return {?}
 */
export function compose(...functions: any[]) {
  return function(arg: any) {
    if (functions.length === 0) {
      return arg;
    }

    const /** @type {?} */ last = functions[functions.length - 1];
    const /** @type {?} */ rest = functions.slice(0, -1);

    return rest.reduceRight((composed, fn) => fn(composed), last(arg));
  };
}
/**
 * @template T, V
 * @param {?} reducerFactory
 * @param {?=} metaReducers
 * @return {?}
 */
export function createReducerFactory<T, V extends Action = Action>(
  reducerFactory: ActionReducerFactory<T, V>,
  metaReducers?: MetaReducer<T, V>[]
): ActionReducerFactory<T, V> {
  if (Array.isArray(metaReducers) && metaReducers.length > 0) {
    return compose.apply(null, [...metaReducers, reducerFactory]);
  }

  return reducerFactory;
}



// WEBPACK FOOTER //
// ../../../modules/store/src/utils.ts